import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { FormikProps } from 'formik';
import React from 'react';

import { Box, EntryList, Flex, Text } from '~/components/blocks';
import { FieldError } from '~/components/partials/PatientBlocks/FieldError';
import { TextField } from '~/components/partials/PatientBlocks/TextField';
import { numberOrHyphenRegexp } from '~/constants/regexp';
import { toHalfNumberWithHyphen } from '~/utils/convert';

import { TermAndPrivacyField } from '../TermAndPrivacyField';
import { Fields } from '../types';

type Props = {
  formik: FormikProps<Fields>;
  isChecking: boolean;
};

const Root = styled('div')(({ theme }) =>
  css({
    maxWidth: '600px',
    margin: `${theme.space.l} auto 0`,
  }),
);

export const FormPane = (props: Props) => {
  const { formik, isChecking } = props;
  const theme = useTheme();

  return (
    <Root>
      <Box>
        <Flex alignItems="center" justifyContent="center">
          <EntryList>
            <EntryList.Head>
              <Text>姓</Text>
            </EntryList.Head>
            <EntryList.Body>
              <TextField
                name="familyName"
                disabled={isChecking}
                error={!!formik.errors.familyName}
                value={formik.values.familyName}
                onChange={formik.handleChange}
                placeholder="患者"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList ml={theme.space.m}>
            <EntryList.Head>
              <Text>名</Text>
            </EntryList.Head>
            <EntryList.Body>
              <TextField
                name="givenName"
                disabled={isChecking}
                error={!!formik.errors.givenName}
                value={formik.values.givenName}
                onChange={formik.handleChange}
                placeholder="太郎"
              />
            </EntryList.Body>
          </EntryList>
        </Flex>
        {formik.errors.familyName && <FieldError error={formik.errors.familyName} />}
        {formik.errors.givenName && <FieldError error={formik.errors.givenName} />}
      </Box>
      <Box mt={theme.space.l}>
        <Flex alignItems="center" justifyContent="center">
          <EntryList>
            <EntryList.Head>
              <Text>セイ</Text>
            </EntryList.Head>
            <EntryList.Body>
              <TextField
                name="phoneticFamilyName"
                disabled={isChecking}
                error={!!formik.errors.phoneticFamilyName}
                value={formik.values.phoneticFamilyName}
                onChange={formik.handleChange}
                placeholder="カンジャ"
              />
            </EntryList.Body>
          </EntryList>
          <EntryList ml={theme.space.m}>
            <EntryList.Head>
              <Text>メイ</Text>
            </EntryList.Head>
            <EntryList.Body>
              <TextField
                name="phoneticGivenName"
                disabled={isChecking}
                error={!!formik.errors.phoneticGivenName}
                value={formik.values.phoneticGivenName}
                onChange={formik.handleChange}
                placeholder="タロウ"
              />
            </EntryList.Body>
          </EntryList>
        </Flex>
        {formik.errors.phoneticFamilyName && (
          <FieldError error={formik.errors.phoneticFamilyName} />
        )}
        {formik.errors.phoneticGivenName &&
          formik.errors.phoneticFamilyName !== formik.errors.phoneticGivenName && (
            <FieldError error={formik.errors.phoneticGivenName} />
          )}
      </Box>
      <Box mt={theme.space.l}>
        <EntryList>
          <EntryList.Head>
            <Text>携帯電話番号</Text>
          </EntryList.Head>
          <EntryList.Body>
            <TextField
              name="phoneNumber"
              disabled={isChecking}
              error={!!formik.errors.phoneNumber}
              value={formik.values.phoneNumber}
              onChange={formik.handleChange}
              onBlur={(e) => {
                if (!numberOrHyphenRegexp.test(e.currentTarget.value)) return;
                formik.setFieldValue('phoneNumber', toHalfNumberWithHyphen(e.currentTarget.value));
              }}
              placeholder="0901234XXXX"
            />
            {formik.errors.phoneNumber && <FieldError error={formik.errors.phoneNumber} />}
          </EntryList.Body>
        </EntryList>
      </Box>
      <Box mt={theme.space.l}>
        <TermAndPrivacyField formikProps={formik} />
      </Box>
    </Root>
  );
};
